/**
 * ACT-UX-2026-001 — what the "Add product" picker (AddCartItemScreen) actually offers a merchant.
 *
 * WHY THIS EXISTS
 * ---------------
 * The September activation audit read "every org created a first product at signup, 30 of 56 never
 * sold". The first half is not a merchant action: `createNewOrganization` (global/Firebase/
 * firebase.ts) seeds ONE sample product, `login.testProduct`, with stock 0. Measured on the 35
 * September orgs with no sale, 26 own nothing else; of the 16 that opened the picker, 14 opened it
 * before creating a product of their own. What they saw was the sample product (stock shown in red)
 * and, for admins, the custom-price line, with no way from that screen to add their real goods. The
 * 2 to 4 second NewSale -> AddCartItem -> NewSale bounce is that screen being closed.
 *
 * This module classifies the list the picker is about to render, so the screen can say what is
 * missing and offer the way to fix it, and so the analytics event can record which state the
 * merchant was in.
 *
 * HOW THE SAMPLE PRODUCT IS RECOGNISED
 * ------------------------------------
 * By name, in every shipped locale. The seed carries no marker field (Firestore or Supabase), and
 * the backend's activation cohort recognises it the same way (`SEED_PRODUCT_NAMES`,
 * apps/atlas-backend/src/modules/growth-attribution/org-activation-cohort.ts). The consequence of a
 * mis-classification is bounded to a hint: a merchant who renamed the sample sees no hint, and one
 * who named a real product exactly "Тестовый товар" sees an extra tip above a list that still
 * works. Nothing here gates selling.
 */

import ru from '../localization/ru/translation'
import en from '../localization/en/translation'

export const ADD_CART_ITEM_CATALOG_STATES = Object.freeze({
  /** No sellable product is active in this store (the list shows only "not found"). */
  EMPTY: 'empty',
  /** The only products active in this store are the signup sample. */
  SAMPLE_ONLY: 'sample_only',
  /** At least one product the merchant created is active in this store. */
  HAS_PRODUCTS: 'has_products',
} as const)

export type AddCartItemCatalogState =
  (typeof ADD_CART_ITEM_CATALOG_STATES)[keyof typeof ADD_CART_ITEM_CATALOG_STATES]

/** The pseudo-product `getProductsForNewSaleByCategoryList` prepends for roles that may sell one. */
export const CUSTOM_PRODUCT_ID = 'customProduct'

/** The signup sample's name in every locale the app ships. */
export const SAMPLE_PRODUCT_NAMES: readonly string[] = Object.freeze(
  [ru.translation.login.testProduct, en.translation.login.testProduct].filter(
    (name): name is string => typeof name === 'string' && name.length > 0,
  ),
)

interface CatalogProduct {
  id?: string | null
  name?: string | null
  activeInStore?: Record<string, boolean | undefined> | null
}

interface CatalogSection {
  data?: CatalogProduct[] | null
}

export const isSampleProduct = (product: CatalogProduct | null | undefined): boolean =>
  typeof product?.name === 'string' && SAMPLE_PRODUCT_NAMES.includes(product.name)

/**
 * Classify the sections the picker hands to `ProductList`.
 *
 * `store` must be the store `ProductList` filters by. The list shows a product only when
 * `activeInStore[store] === true` (ProductList `filterByStore`), so the same predicate is applied
 * here: a product that exists but is switched off in this store is not something the merchant can
 * pick. Only `'all'` skips the filter, exactly as in `ProductList`: with no store, `ProductList`
 * still filters (by `activeInStore[undefined]`) and shows no real product, so neither is counted here.
 */
export function getAddCartItemCatalogState(
  sections: CatalogSection[] | null | undefined,
  store: string | null | undefined,
): AddCartItemCatalogState {
  let sampleCount = 0
  for (const section of sections ?? []) {
    for (const product of section?.data ?? []) {
      if (!product || product.id === CUSTOM_PRODUCT_ID) continue
      if (store !== 'all' && (!store || product.activeInStore?.[store] !== true)) continue
      if (!isSampleProduct(product)) return ADD_CART_ITEM_CATALOG_STATES.HAS_PRODUCTS
      sampleCount += 1
    }
  }
  return sampleCount > 0 ? ADD_CART_ITEM_CATALOG_STATES.SAMPLE_ONLY : ADD_CART_ITEM_CATALOG_STATES.EMPTY
}

// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Analytics
// ─────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * One event per picker session, sent when the screen goes away. Documented in
 * `docs/technical/analytics-add-cart-item.md`.
 *
 * It exists because a pick and a dismissal produce the same screen trail (the picker closes itself
 * on a single-select tap), so "they bounced" could not be told apart from "they added a product and
 * then stopped". Whether the pick then made it into the cart is NOT this event's claim: the stock
 * guard in `addNewCartItem` can still refuse it, and that refusal is reported by
 * `sale_checkout_blocked` (stage `cart`), which this module deliberately does not touch.
 */
export const ADD_CART_ITEM_CLOSED_EVENT = 'add_cart_item_closed'

export const ADD_CART_ITEM_OUTCOMES = Object.freeze({
  /** Single-select: a product was tapped (the picker closes itself). */
  PRODUCT_PICKED: 'product_picked',
  /** Multi-select: the checkmark confirmed at least one new product. */
  PRODUCTS_PICKED: 'products_picked',
  /** Closed without handing any product to the cart, with nothing ticked. */
  DISMISSED: 'dismissed',
  /**
   * Multi-select: closed with new products ticked but never confirmed, so none reached the cart.
   * Multi-select is the signup default, and leaving the picker any way but the confirm action
   * drops the ticks silently.
   */
  DISMISSED_WITH_SELECTION: 'dismissed_with_selection',
} as const)

export type AddCartItemOutcome = (typeof ADD_CART_ITEM_OUTCOMES)[keyof typeof ADD_CART_ITEM_OUTCOMES]

/**
 * ATL-1921 — the navigation action that removed the picker, from its `beforeRemove` event.
 *
 * Justore's admin on one iPhone 15 closed the picker 35 times in 14 days without a single pick,
 * while the same account on two other phones, and every other iOS 26.6 device in the fleet, picked
 * normally. The screen trail cannot say what closed it. This separates the mechanisms:
 *  - `POP`      — native-stack's own dismissal (react-native-screens `onDismissed` dispatches
 *                 `StackActions.pop(dismissCount)`): the swipe-back gesture or the native header
 *                 back button. No JS code in the app pops this screen with `pop`.
 *  - `GO_BACK`  — JS `navigation.goBack()`: the single-select tap or the multi-select confirm.
 *  - `NAVIGATE` / `POP_TO` / `POP_TO_TOP` / `RESET` / `REPLACE` — a programmatic navigation elsewhere.
 *  - `none`     — no `beforeRemove` reached the screen: the navigator itself was torn down (the
 *                 signed-in stack re-rendered as a spinner, a reload) rather than this route removed.
 *  - `other`    — any other action type; never forwarded as free text.
 *
 * The meaning is platform-dependent, so read it per `$os_name`: on Android the hardware back button
 * also calls `goBack()` (@react-navigation/native useBackButton), so `GO_BACK` there is a pick OR a
 * dismissal — `outcome` tells them apart; on web the browser back button dispatches `RESET`
 * (useLinking → resetRoot). The iOS reading above is exact.
 */
export const ADD_CART_ITEM_REMOVAL_ACTIONS = Object.freeze([
  'POP', 'GO_BACK', 'NAVIGATE', 'POP_TO', 'POP_TO_TOP', 'RESET', 'REPLACE', 'none', 'other',
] as const)

export type AddCartItemRemovalAction = (typeof ADD_CART_ITEM_REMOVAL_ACTIONS)[number]

/** Counts above this are reported as this value: the question is "none, one, a few, many". */
export const ADD_CART_ITEM_COUNT_CAP = 50

export interface AddCartItemClosedInput {
  outcome: string
  catalogStateOnOpen: string
  catalogStateOnClose: string
  createProductTapped: boolean
  multiSelect: boolean
  /** `beforeRemove`'s `e.data.action`, or null when none fired. */
  removalAction?: { type?: unknown, payload?: unknown } | null
  /** How many times a product row (or its checkbox) handed a product to the picker this visit. */
  itemPressCount?: unknown
  /** How many native swipe-back gestures were started and abandoned this visit (`gestureCancel`). */
  gestureCancelCount?: unknown
  /** How many times a category filter (or «Все») in the list header was tapped this visit. */
  categoryPressCount?: unknown
  /** What `createAddCartItemTouchTracker().snapshot()` returned when the picker unmounted. */
  touch?: AddCartItemTouchSnapshot | null
}

export interface AddCartItemClosedProperties {
  outcome: AddCartItemOutcome
  catalog_state_on_open: AddCartItemCatalogState
  catalog_state_on_close: AddCartItemCatalogState
  create_product_tapped: boolean
  multi_select: boolean
  removal_action: AddCartItemRemovalAction
  /** Screens a native `POP` removed at once (`dismissCount`); null for every other action. */
  removal_pop_count: number | null
  item_press_count: number
  gesture_cancel_count: number
  category_press_count: number
  content_touch_count: number
  close_touch_state: AddCartItemCloseTouchState
  close_touch_from_edge: boolean | null
  close_touch_dx: number | null
}

/**
 * ATL-1921 (second increment) — what the merchant's finger was doing when the close began.
 *
 * The first increment showed the iPhone 15's closes are native `POP`s with no product press. On iOS
 * a `POP` is either the native header back button or a swipe-back, and nothing in React Navigation
 * says which: the header button has no JS callback on iOS (`onHeaderBackButtonClicked` is
 * Android-only in react-native-screens 4.11). What JS CAN see is the picker's own content. The
 * header is a native navigation bar outside it, so a header-button close starts with no content
 * touch in progress; a swipe-back is a finger on the content (from the left edge, or anywhere on
 * iOS 26's full-content back gesture) that is still down, or was just cancelled by UIKit taking
 * the gesture over, when the close transition starts (`transitionStart` with `closing: true`,
 * which native-stack emits from the screen's `viewWillDisappear`).
 *
 * The tracker records facts, never a verdict, because the order in which iOS delivers the touch
 * cancel and `viewWillDisappear` to JS has not been observed on a device:
 *  - `active`              a content touch was down when the close started;
 *  - `cancelled_recently`  the last content touch ended in a cancel at most RECENT_TOUCH_MS before;
 *  - `ended_recently`      the last content touch was lifted normally at most RECENT_TOUCH_MS before;
 *  - `none`                no content touch in that window;
 *  - `not_observed`        no closing `transitionStart` reached the picker before it unmounted.
 * A close that is called off discards its snapshot: a cancelled swipe (`gestureCancel` — the native
 * stack sends no `closing: false` for it), a screen pushed over the picker (its `closing: true`
 * arrives once the pushed route is already on top), and a return to the picker. So the snapshot is
 * only ever the native close that removed the picker: it is meaningful for `removal_action = POP`
 * on iOS. A close the app makes itself (`goBack`) unmounts the picker before the native
 * transition, and web emits no transition events, so both report `not_observed`.
 */
export const ADD_CART_ITEM_CLOSE_TOUCH_STATES = Object.freeze([
  'active', 'cancelled_recently', 'ended_recently', 'none', 'not_observed',
] as const)

export type AddCartItemCloseTouchState = (typeof ADD_CART_ITEM_CLOSE_TOUCH_STATES)[number]

/** "Just before the close": a lifted or cancelled touch older than this is not the close's. */
export const ADD_CART_ITEM_RECENT_TOUCH_MS = 1000
/** UIKit's edge swipe-back starts within ~20 pt of the left edge; a little slack for the finger. */
export const ADD_CART_ITEM_EDGE_ZONE_PT = 24
/** Horizontal travel above this is reported as this value (points). */
export const ADD_CART_ITEM_DX_CAP = 1000

export interface AddCartItemTouchSnapshot {
  contentTouchCount: number
  closeTouchState: AddCartItemCloseTouchState
  closeTouchFromEdge: boolean | null
  closeTouchDx: number | null
}

/** The part of a React Native touch event the tracker reads. */
interface TouchEventLike {
  nativeEvent?: { identifier?: unknown, pageX?: unknown } | null
}

interface TrackedTouch {
  identifier: unknown
  startX: number | null
  lastX: number | null
}

const pageXOf = (event: TouchEventLike | null | undefined): number | null => {
  const x = event?.nativeEvent?.pageX
  return typeof x === 'number' && Number.isFinite(x) ? x : null
}

const dxOf = (touch: TrackedTouch): number | null => {
  if (touch.startX === null || touch.lastX === null) return null
  const dx = Math.round(touch.lastX - touch.startX)
  return Math.max(-ADD_CART_ITEM_DX_CAP, Math.min(ADD_CART_ITEM_DX_CAP, dx))
}

const fromEdgeOf = (touch: TrackedTouch): boolean | null =>
  touch.startX === null ? null : touch.startX <= ADD_CART_ITEM_EDGE_ZONE_PT

/**
 * Follows the first finger on the picker's content and snapshots it when a close transition
 * starts. Fed by passive `onTouch*` props on the screen's root view, which observe touches without
 * taking the responder, so no tap, scroll or gesture behaves differently. `now` is injectable for
 * tests.
 */
export function createAddCartItemTouchTracker(now: () => number = Date.now) {
  let contentTouchCount = 0
  let current: TrackedTouch | null = null
  let last: (TrackedTouch & { how: 'end' | 'cancel', at: number }) | null = null
  let closing: Omit<AddCartItemTouchSnapshot, 'contentTouchCount'> | null = null

  const isCurrent = (event: TouchEventLike | null | undefined): boolean =>
    current !== null && current.identifier === event?.nativeEvent?.identifier

  const finish = (event: TouchEventLike | null | undefined, how: 'end' | 'cancel') => {
    if (!current || !isCurrent(event)) return
    const x = pageXOf(event)
    last = { ...current, lastX: x ?? current.lastX, how, at: now() }
    current = null
  }

  return {
    touchStart(event: TouchEventLike | null | undefined) {
      contentTouchCount += 1
      // A second finger is counted but not followed: the close is decided by the first one.
      if (current) return
      const x = pageXOf(event)
      current = { identifier: event?.nativeEvent?.identifier, startX: x, lastX: x }
    },
    touchMove(event: TouchEventLike | null | undefined) {
      if (!current || !isCurrent(event)) return
      const x = pageXOf(event)
      if (x !== null) current.lastX = x
    },
    touchEnd(event: TouchEventLike | null | undefined) {
      finish(event, 'end')
    },
    touchCancel(event: TouchEventLike | null | undefined) {
      finish(event, 'cancel')
    },
    /** `transitionStart` with `closing: true`. */
    closeStarted() {
      if (current) {
        closing = { closeTouchState: 'active', closeTouchFromEdge: fromEdgeOf(current), closeTouchDx: dxOf(current) }
        return
      }
      if (last && now() - last.at <= ADD_CART_ITEM_RECENT_TOUCH_MS) {
        closing = {
          closeTouchState: last.how === 'cancel' ? 'cancelled_recently' : 'ended_recently',
          closeTouchFromEdge: fromEdgeOf(last),
          closeTouchDx: dxOf(last),
        }
        return
      }
      closing = { closeTouchState: 'none', closeTouchFromEdge: null, closeTouchDx: null }
    },
    /** The close did not happen (the picker is showing again, the swipe was cancelled) or was not a close (covered). */
    closeAbandoned() {
      closing = null
    },
    snapshot(): AddCartItemTouchSnapshot {
      return {
        contentTouchCount,
        ...(closing ?? { closeTouchState: 'not_observed', closeTouchFromEdge: null, closeTouchDx: null }),
      }
    },
  }
}

const CLOSE_TOUCH_STATE_VALUES: readonly string[] = ADD_CART_ITEM_CLOSE_TOUCH_STATES

const normalizeTouch = (touch: AddCartItemClosedInput['touch']) => {
  const state = touch?.closeTouchState
  const known = typeof state === 'string' && CLOSE_TOUCH_STATE_VALUES.includes(state)
  const dx = touch?.closeTouchDx
  return {
    content_touch_count: normalizeCount(touch?.contentTouchCount),
    close_touch_state: (known ? state : 'not_observed') as AddCartItemCloseTouchState,
    close_touch_from_edge: known && typeof touch?.closeTouchFromEdge === 'boolean' ? touch.closeTouchFromEdge : null,
    close_touch_dx: known && typeof dx === 'number' && Number.isInteger(dx) && Math.abs(dx) <= ADD_CART_ITEM_DX_CAP ? dx : null,
  }
}

interface CapturingClient {
  capture?: (event: string, properties?: Record<string, unknown>) => unknown
}

const OUTCOME_VALUES: readonly string[] = Object.values(ADD_CART_ITEM_OUTCOMES)
const STATE_VALUES: readonly string[] = Object.values(ADD_CART_ITEM_CATALOG_STATES)

const normalizeOutcome = (value: string): AddCartItemOutcome =>
  (OUTCOME_VALUES.includes(value) ? value : ADD_CART_ITEM_OUTCOMES.DISMISSED) as AddCartItemOutcome

// An unknown state is reported as EMPTY rather than invented: it is the state that asks the most
// of the merchant, so a bad value cannot make the catalog look healthier than it was.
const normalizeState = (value: string): AddCartItemCatalogState =>
  (STATE_VALUES.includes(value) ? value : ADD_CART_ITEM_CATALOG_STATES.EMPTY) as AddCartItemCatalogState

const REMOVAL_ACTION_VALUES: readonly string[] = ADD_CART_ITEM_REMOVAL_ACTIONS

const normalizeRemovalAction = (action: AddCartItemClosedInput['removalAction']): AddCartItemRemovalAction => {
  if (!action) return 'none'
  const type = action.type
  if (typeof type === 'string' && type !== 'none' && type !== 'other' && REMOVAL_ACTION_VALUES.includes(type)) {
    return type as AddCartItemRemovalAction
  }
  return 'other'
}

const normalizeCount = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0 ? Math.min(value, ADD_CART_ITEM_COUNT_CAP) : 0

const popCountOf = (action: AddCartItemClosedInput['removalAction']): number | null => {
  if (normalizeRemovalAction(action) !== 'POP') return null
  const payload = action?.payload
  const count = payload && typeof payload === 'object' ? (payload as { count?: unknown }).count : undefined
  // StackActions.pop() with no argument removes one screen.
  return count === undefined ? 1 : normalizeCount(count)
}

/**
 * Build the closed property bag and send it. No product names, ids or prices leave the device,
 * nor any measure of the catalog: the states above, the removing action's type, capped tap, touch
 * and gesture counts, and the close-touch facts (a state, an edge flag, a horizontal distance) are
 * the whole payload. Never throws (ATL-901: analytics is never
 * load-bearing, and this runs while the screen is unmounting).
 */
export function captureAddCartItemClosed(
  postHog: CapturingClient | null | undefined,
  input: AddCartItemClosedInput,
): { captured: boolean, properties: AddCartItemClosedProperties } {
  const properties: AddCartItemClosedProperties = {
    outcome: normalizeOutcome(input.outcome),
    catalog_state_on_open: normalizeState(input.catalogStateOnOpen),
    catalog_state_on_close: normalizeState(input.catalogStateOnClose),
    create_product_tapped: input.createProductTapped === true,
    multi_select: input.multiSelect === true,
    removal_action: normalizeRemovalAction(input.removalAction),
    removal_pop_count: popCountOf(input.removalAction),
    item_press_count: normalizeCount(input.itemPressCount),
    gesture_cancel_count: normalizeCount(input.gestureCancelCount),
    category_press_count: normalizeCount(input.categoryPressCount),
    ...normalizeTouch(input.touch),
  }
  try {
    if (typeof postHog?.capture !== 'function') return { captured: false, properties }
    postHog.capture(ADD_CART_ITEM_CLOSED_EVENT, { ...properties })
    return { captured: true, properties }
  } catch (error) {
    console.warn('[analytics] add_cart_item_closed could not be captured', error)
    return { captured: false, properties }
  }
}
